import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { blueArrowIcon, img } from "../../constants/assets";
import GlobalConnected, {
  FeatureIcon,
} from "../../components/GlobalConnected";
import BrandsMarquee from "../../components/BrandsMarquee";
import ScrollReveal from "../../components/ScrollReveal";
import BlurRevealText from "../../components/BlurRevealText";
import EmpowerBusinesses from "../../components/EmpowerBusinesses";
import PlatformPillars from "../../components/PlatformPillars";
import PartnerModal from "../../components/PartnerModal";
import { useBookDemo } from "../../context/BookDemoContext";

const howItWorksIcons = [
  "create-icon-user.svg",
  "customisze-icon.svg",
  "customize2.svg",
  "launch-icon.svg",
];

const aiCardIcons = ["airecive.svg", "routing.svg", "fraud.svg", "demand.svg"];
const ctaFeatureIcons = ["globe", "tower", "shield", "code"];
const heroStatIcons = ["globe", "tower", "bolt", "shield"];

function CtaRocketIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.03-2.91l-2.91-2.91c-.78-.73-2.07-.74-2.91-.03z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M9 12H4s.55-3.03 2-5c1.62-2.2 5-3 5-3M15 12h5s-.55-3.03-2-5c-1.62-2.2-5-3-5-3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CtaHeadsetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 11v4a2 2 0 0 0 2 2h1v-8H5a2 2 0 0 0-2 2zM19 11v4a2 2 0 0 1-2 2h-1v-8h1a2 2 0 0 1 2 2z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M7 15v2a5 5 0 0 0 10 0v-2"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M12 3a5 5 0 0 0-5 5v3h10V8a5 5 0 0 0-5-5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Home() {
  const { t } = useTranslation();
  const { openBookDemo } = useBookDemo();
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);

  const heroStats = t("home.heroStats", { returnObjects: true });
  const howItWorksSteps = t("home.howItWorksSteps", { returnObjects: true });
  const aiCards = t("home.aiCards", { returnObjects: true });
  const platformFeatures = t("home.platformFeatures", { returnObjects: true });
  const ctaFeatures = t("ctaBanner.features", { returnObjects: true });

  const ctaTitleSegments = [
    { text: t("ctaBanner.titleBefore") },
    {
      text: t("ctaBanner.titleHighlight"),
      className: "cta-banner-accent",
      breakAfter: true,
    },
    { text: t("ctaBanner.titleAfter") },
  ];

  return (
    <>
      <section className="hero-section">
        <div className="container py-2">
          <div className="row align-items-center" style={{ minHeight: 400 }}>
            <div className="col-md-6 d-flex hero-content mt-">
              <div className="animate__animated animate__fadeInLeftBig animate__slow">
                <p className="banner-sub-heading p-2">{t("home.heroOverline")}</p>
                <h1>
                  {t("home.heroTitle1")}
                  <br />
                  <span className="gradient-text-span">
                    {t("home.heroTitleHighlight")}
                  </span>{" "}
                  {t("home.heroTitle2")}
                  <br />
                  {t("home.heroTitle3")}
                </h1>
                <p className="pera-cs">{t("home.heroDesc")}</p>
                <div className="btn-group gap-3">
                  <button
                    className="btn-cs btn-booking py-3 fw-normal"
                    type="button"
                    onClick={openBookDemo}
                  >
                    {t("common.bookTechnicalDemo")}{" "}
                    <span className="img-fluid">
                      <img src={img("arrow.svg")} alt="arrow" />
                    </span>
                  </button>
                  <Link
                    className="why-choose-cta bg-transparent"
                    to="/solutions"
                  >
                    {t("common.explorePlatform")}
                    <span className="img-fluid">
                      <img src={img("Border.svg")} alt="" />
                    </span>
                  </Link>
                </div>
                <ul className="hero-stats">
                  {heroStats.map((stat, index) => (
                    <li className="hero-stat" key={stat.label}>
                      <span className="hero-stat-icon">
                        <FeatureIcon type={heroStatIcons[index]} />
                      </span>
                      <div>
                        <strong className="hero-stat-value">{stat.value}</strong>
                        <span className="hero-stat-label">{stat.label}</span>
                        <span className="hero-stat-desc">{stat.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-md-6 hero-visual-col">
              <div className="hero-visual">
                <img
                  className="hero-visual-img"
                  src={img("heroRight.png")}
                  alt={t("home.heroImageAlt")}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="working py-50">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <p className="brands-heading">
                {t("common.trustedBy")} <span>{t("common.worldwide")}</span>
              </p>
              <BrandsMarquee />
            </div>
          </div>
        </div>
      </section>

      <section className="platform-section py-50">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-5">
              <ScrollReveal from="left" className="platform-content">
                <div className="banner-sub-heading p-2 mb-3">
                  {t("home.platformOverline")}
                </div>
                <h1 className="inner-heading">
                  {t("home.platformTitle1")}
                  <br />
                  {t("home.platformTitle2")}{" "}
                  <span className="gradient-text-span">
                    {t("home.platformTitleHighlight")}
                  </span>
                </h1>
                <p className="pera-cs">{t("home.platformDesc")}</p>

                <ul className="platform-list">
                  {platformFeatures.map((feature) => (
                    <li key={feature}>
                      <span className="platform-check" aria-hidden="true">
                        <img src={img("check.svg")} alt="" />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button className="why-choose-cta" type="button">
                  {t("home.seePlatform")}{" "}
                  <span className="img-fluid">
                    <img src={blueArrowIcon} alt="" />
                  </span>
                </button>
              </ScrollReveal>
            </div>

            <div className="col-lg-7">
              <ScrollReveal className="platform-media">
                <img
                  className="platform-image"
                  src={img("admindesign.png")}
                  alt={t("home.platformImageAlt")}
                />
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <PlatformPillars />

      <section className="experience-section py-50">
        <div className="container">
          <div className="row  g-5">
            <div className="col-lg-5">
              <ScrollReveal from="left" className="experience-content mt-2">
                <div className="banner-sub-heading p-2 mb-3">
                  {t("home.experienceOverline")}
                </div>
                <h1 className="inner-heading">
                  <span className="gradient-text-span">
                    {t("home.experienceTitle1")}
                  </span>{" "}
                  {t("home.experienceTitle2")}
                  <br />
                  <span className="gradient-text-span">
                    {t("home.experienceTitleHighlight")}
                  </span>
                </h1>
                <p className="pera-cs">{t("home.experienceDesc")}</p>
              </ScrollReveal>
            </div>

            <div className="col-lg-7">
              <ScrollReveal className="experience-media">
                <img
                  className="experience-image"
                  src={img("chooseplan.svg")}
                  alt={t("home.experienceImageAlt")}
                />
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <section className="why-choose-section py-50">
        <div className="container">
          <div className="row align-items-center g-4 g-lg-5">
            <div className="col-lg-4 ">
              <ScrollReveal from="left" className="why-choose-content">
                <div className="banner-sub-heading p-2 mb-3">
                  {t("home.whyChooseOverline")}
                </div>
                <h2 className="inner-heading">{t("home.whyChooseTitle")}</h2>
                <p className="pera-cs">{t("home.whyChooseDesc")}</p>
                <button className="why-choose-cta" type="button">
                  {t("home.exploreAi")}{" "}
                  <span className="img-fluid">
                    <img src={blueArrowIcon} alt="" />
                  </span>
                </button>
              </ScrollReveal>
            </div>

            <div className="col-lg-8">
              <div className="row g-3 g-xl-4 why-choose-cards">
                {aiCards.map((card, index) => (
                  <div className="col-6 col-xl-3" key={card.title}>
                    <article className="why-choose-card">
                      <div className="why-choose-icon">
                        <img src={img(aiCardIcons[index])} alt="" />
                      </div>
                      <h3 className="why-choose-card-title">{card.title}</h3>
                      <p className="why-choose-card-text">{card.description}</p>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <GlobalConnected label={t("home.globalLabel")} />

      <section className="how-it-works-section py-50">
        <div className="container">
          <div className="how-it-works-header text-center">
            <div className="banner-sub-heading mx-auto mb-3">
              {t("home.howItWorksOverline")}
            </div>
            <BlurRevealText
              as="h2"
              className="inner-heading"
              lines={[t("home.howItWorksTitle")]}
            />
          </div>

          <div className="how-it-works-steps">
            {howItWorksSteps.map((step, index) => (
              <div className="how-it-works-step-wrap" key={step.title}>
                <article className="how-it-works-card">
                  <div className="how-it-works-icon">
                    <img src={img(howItWorksIcons[index])} alt="" />
                  </div>
                  <div className="how-it-works-card-content">
                    <h3 className="how-it-works-card-title">{step.title}</h3>
                    <p className="how-it-works-card-text">{step.description}</p>
                  </div>
                </article>
                {index < howItWorksSteps.length - 1 && (
                  <span className="how-it-works-arrow" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <EmpowerBusinesses />

      <section className="cta-banner-section">
        <div className="container">
          <div className="cta-banner">
            <div className="row align-items-center g-4">
              <div className="col-lg-7">
                <p className="cta-banner-label">
                  {t("ctaBanner.label")}
                </p>
                <BlurRevealText
                  as="h2"
                  className="cta-banner-title"
                  segments={ctaTitleSegments}
                />
                <p className="cta-banner-text">
                  <Trans
                    i18nKey="ctaBanner.subtitle"
                    components={{ 1: <span className="cta-banner-accent" /> }}
                  />
                </p>
                <div className="cta-banner-features">
                  {ctaFeatures.map((item, index) => (
                    <div className="cta-banner-feature" key={item.label}>
                      <FeatureIcon type={ctaFeatureIcons[index]} />
                      <div>
                        <strong className="cta-banner-stat">{item.stat}</strong>
                        <span className="cta-banner-feature-label">
                          {item.label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-lg-5">
                <div className="cta-banner-actions">
                  <button
                    className="cta-btn-primary"
                    type="button"
                    onClick={() => setIsPartnerModalOpen(true)}
                  >
                    <span className="cta-btn-icon">
                      <CtaRocketIcon />
                    </span>
                    <span className="cta-btn-label">{t("common.startNow")}</span>
                    <span className="cta-btn-icon">
                      <img src={img("arrow.svg")} alt="" />
                    </span>
                  </button>
                  <button
                    className="cta-btn-secondary"
                    type="button"
                    onClick={openBookDemo}
                  >
                    <span className="cta-btn-icon">
                      <CtaHeadsetIcon />
                    </span>
                    <span className="cta-btn-label">{t("common.talkToSales")}</span>
                    <span className="cta-btn-icon">
                      <img src={img("arrow.svg")} alt="" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnerModal
        isOpen={isPartnerModalOpen}
        onClose={() => setIsPartnerModalOpen(false)}
      />
    </>
  );
}

export default Home;
